import cn from 'classnames'
import { find, groupBy, uniq } from 'lodash'
import { array } from 'prop-types'
import React, { Fragment, useContext } from 'react'

import { DropdownItem } from '@mc/design-system'
import { getPageSource } from '@mc/track-event'

import Articles from '../Articles'
import { Instructor } from '../Instructor'
import { Lesson } from '../Lesson'
import NoResultsFound from '../NoResultsFound'
import { Camp } from '../Camp'
import {
  getArticleIndex,
  getCampIndex,
  getChapterIndex,
  getCourseIndex,
} from '../../getIndexNames'
import { SearchContext } from '../../SearchContext'

import classes from './Results.module.scss'

const campHasPriority = (camp) =>
  camp._highlightResult.instructor_names?.some(
    (instructor) => instructor.matchLevel !== 'none',
  ) || camp._highlightResult.camp_title?.matchLevel !== 'none'

const instructorHasPriority = (instructor) =>
  instructor._highlightResult.headline?.matchLevel !== 'none' ||
  instructor._highlightResult.instructor?.matchLevel !== 'none' ||
  instructor._highlightResult.category?.matchLevel !== 'none'

const getLessonsByCourseId = (id, lessons) =>
  groupBy(lessons.hits, 'class_id')[id]

const lessonHasTitleMatch = (lessons) =>
  typeof find(
    lessons,
    (lesson) => lesson._highlightResult.lesson_title?.matchLevel !== 'none',
  ) !== 'undefined'

const Results = ({ hits }) => {
  const { category, courses, query } = useContext(SearchContext)
  const hasCategory = !!category

  const getHitsByIndex = (_index) => hits.find(({ index }) => index === _index)

  const renderArticles = () => {
    const algoliaArticles = getHitsByIndex(getArticleIndex())
    if (
      !hasCategory &&
      algoliaArticles &&
      algoliaArticles.hits &&
      algoliaArticles.hits.length > 0
    ) {
      return (
        <div className={classes.result} key='articles'>
          <Articles articles={algoliaArticles.hits} />
        </div>
      )
    }
    return null
  }

  /*
    Rank is used to display certain results higher in the search
    ------------------------------------------------------------
    rank 0 - camp title or camp instructor name match
    rank 1 - camp module has match in title (To be implemented, currently default)
  */

  const renderCamps = () => {
    const algoliaCamps = getHitsByIndex(getCampIndex())

    if (!algoliaCamps || algoliaCamps.hits?.length === 0) {
      return []
    }

    return algoliaCamps.hits.map((camp) => ({
      components: (
        <Fragment key={`camp-${camp.camp_slug}`}>
          <DropdownItem className={classes.result}>
            <Camp
              campId={camp.id}
              hit={camp}
              image={camp.primary_1x1}
              instructorNames={camp.instructor_names}
              query={query}
              title={camp.camp_title}
              url={`/sessions/classes/${camp.camp_slug}`}
              pageSource={getPageSource()}
            />
          </DropdownItem>
          {/* add Module items here */}
        </Fragment>
      ),
      rank: (() => (campHasPriority(camp) ? 0 : 1))(),
    }))
  }

  const renderLessons = (lessons, courseId, courseSlug) => {
    if (!lessons) return null
    return (
      <Fragment>
        {lessons.map((lesson) => (
          <DropdownItem key={lesson.objectID} className={classes.lessonResults}>
            <Lesson
              query={query}
              courseId={courseId}
              chapterId={lesson.objectID}
              lesson={lesson}
              courseSlug={courseSlug}
              pageSource={getPageSource()}
            />
          </DropdownItem>
        ))}
      </Fragment>
    )
  }

  /*
    Rank is used to display certain results higher in the search
    ------------------------------------------------------------
    rank 0 - instructor title/name/category match
    rank 1 - lesson has match in title
    rank 2 - subchapter has match in title
    rank 3 - match in course/lesson description
  */

  // pair lessons to it's parent course when a search matches a lesson AND course
  const renderMatchedCoursesWithMatchedLessons = () => {
    const algoliaCourses = getHitsByIndex(getCourseIndex())
    const algoliaLessons = getHitsByIndex(getChapterIndex())
    if (algoliaCourses?.hits.length > 0) {
      return algoliaCourses.hits.map((instructor) => {
        const { id } = instructor
        const slug = instructor['class slug']
        const lessons = getLessonsByCourseId(instructor.id, algoliaLessons)
        const lessonTitleMatch = lessonHasTitleMatch(lessons)
        const instructorPriority = instructorHasPriority(instructor)
        return {
          rank: (() => {
            if (instructorPriority) return 0
            else if (lessonTitleMatch) return 1
            return 2
          })(),
          components: (
            <Fragment key={id}>
              <DropdownItem className={classes.result}>
                <Instructor
                  query={query}
                  hit={instructor}
                  hitId={instructor.id}
                  headshot={instructor.headshot}
                  headshotAltText={instructor.headshotAltText}
                  url={instructor.url}
                  skill={instructor.skill}
                  isMultiInstructor={instructor.is_multi_instructor}
                  multiInstructorName={instructor.multi_instructor_name}
                  slug={slug}
                  pageSource={getPageSource()}
                />
              </DropdownItem>
              {!hasCategory && renderLessons(lessons, id, slug)}
            </Fragment>
          ),
        }
      })
    }
    return []
  }

  /*
    pair lessons to it's parent course when a search matches ONLY a lesson.
    uses a raw courses list to display which course the lesson belongs to.
    this is needed because if the course doesn't match it won't be in hits.
  */
  const renderMatchedLessonsWithUnmatchedCourses = () => {
    const algoliaCourses = getHitsByIndex(getCourseIndex())
    const algoliaLessons = getHitsByIndex(getChapterIndex())
    const hasLessonHits = algoliaLessons?.hits
    if (!hasCategory && hasLessonHits && courses && algoliaCourses) {
      // course id's of algolia courses, filter these out later
      const algoliaCourseIds = algoliaCourses.hits.map((hit) => hit.id)
      // course id's of lesson results
      const courseIds = uniq(algoliaLessons.hits.map((hit) => hit.class_id))
      // filter out courses that algolia picked up and
      // add courses from lesson results
      const filteredCourses = courses.filter(
        (course) =>
          !algoliaCourseIds.includes(course.id) &&
          courseIds.includes(course.id),
      )

      return filteredCourses.map((instructor) => {
        const { id, headshot, headshotAltText } = instructor
        const slug = instructor['class slug']
        const lessons = getLessonsByCourseId(id, algoliaLessons)
        const lessonTitleMatch = lessonHasTitleMatch(lessons)

        return {
          rank: (() => {
            if (lessonTitleMatch) return 1
            return 2
          })(),
          components: (
            <Fragment key={id}>
              <DropdownItem className={classes.result}>
                <Instructor
                  query={query}
                  headshot={headshot}
                  headshotAltText={headshotAltText}
                  name={instructor.instructor}
                  hitId={instructor.id}
                  headline={instructor.headline}
                  url={`/classes/${slug}`}
                  slug={slug}
                  pageSource={getPageSource()}
                />
              </DropdownItem>
              {renderLessons(lessons, id, slug)}
            </Fragment>
          ),
        }
      })
    }
    return []
  }

  const renderResults = () => {
    const results = [
      ...renderCamps(),
      ...renderMatchedCoursesWithMatchedLessons(),
      ...renderMatchedLessonsWithUnmatchedCourses(),
    ]

    const articles = renderArticles()

    // sort by search rank (commented above) and add articles at the end
    const rankedResults = results
      .sort((a, b) => a.rank - b.rank)
      .map((result) => result.components)

    return articles ? rankedResults.concat(articles) : rankedResults
  }

  const results = renderResults()

  return (
    <div
      className={cn(classes.wrapper, {
        [classes.showHighlight]: !hasCategory,
      })}
    >
      {results.length ? results : <NoResultsFound query={query} />}
    </div>
  )
}

Results.propTypes = {
  hits: array,
}

export default Results
